import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, X, Loader2 } from 'lucide-react';

/**
 * Google Places Autocomplete with DEBOUNCE.
 *
 * Uses AutocompleteService (programmatic) instead of the Autocomplete widget
 * so we control exactly when API calls fire. Waits 500ms after user stops
 * typing before querying Google — saves API calls and cost.
 *
 * Returns { address, city, state, country, pincode, lat, lng } on selection.
 */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEBOUNCE_MS = 500;

// Load script once (shared across instances)
let scriptLoaded = false;
let scriptLoading = false;
const callbacks = [];

function loadScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { scriptLoaded = true; resolve(); return; }
    if (scriptLoading) { callbacks.push({ resolve, reject }); return; }
    if (!GOOGLE_MAPS_API_KEY) { reject(new Error('API key missing')); return; }

    scriptLoading = true;
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => { scriptLoaded = true; scriptLoading = false; resolve(); callbacks.forEach(c => c.resolve()); callbacks.length = 0; };
    s.onerror = (e) => { scriptLoading = false; reject(e); callbacks.forEach(c => c.reject(e)); callbacks.length = 0; };
    document.head.appendChild(s);
  });
}

export default function AddressAutocomplete({
  value = '',
  onChange,
  onClear,
  placeholder = 'Search for a location...',
  label = 'Location',
  required = false,
  disabled = false,
  country = 'in',
  className = '',
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);
  const dummyDiv = useRef(null);

  // Sync external value
  useEffect(() => { setInputValue(value); }, [value]);

  // Load Google Maps script
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadScript();
        if (cancelled) return;
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        if (!dummyDiv.current) dummyDiv.current = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv.current);
        setIsReady(true);
      } catch (err) {
        if (!cancelled) setError('Address search unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search — waits 500ms after user stops typing
  const searchPlaces = useCallback((query) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (!query || query.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    debounceTimer.current = setTimeout(() => {
      autocompleteService.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country },
          types: ['geocode', 'establishment'],
        },
        (predictions, status) => {
          setLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        }
      );
    }, DEBOUNCE_MS);
  }, [country]);

  // Handle input typing
  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setInputValue(val);
    searchPlaces(val);
  }, [searchPlaces]);

  // Handle suggestion click — get full place details
  const handleSelect = useCallback((prediction) => {
    if (!placesService.current) return;

    setShowDropdown(false);
    setLoading(true);
    setInputValue(prediction.description);

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'address_components', 'name'],
      },
      (place, status) => {
        setLoading(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry) return;

        const components = place.address_components || [];
        const get = (type) => {
          const c = components.find((comp) => comp.types.includes(type));
          return c?.long_name || '';
        };

        const locationData = {
          address: place.formatted_address || prediction.description,
          name: place.name || '',
          city: get('locality') || get('administrative_area_level_2') || get('sublocality_level_1') || '',
          state: get('administrative_area_level_1'),
          country: get('country'),
          pincode: get('postal_code'),
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        setInputValue(locationData.address);
        if (onChange) onChange(locationData);
      }
    );
  }, [onChange]);

  // Handle clear
  const handleClear = useCallback(() => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (onClear) onClear();
    if (onChange) onChange({ address: '', name: '', city: '', state: '', country: '', pincode: '', lat: null, lng: null });
  }, [onChange, onClear]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
          placeholder={placeholder}
          disabled={disabled || !isReady}
          required={required}
          autoComplete="off"
          className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed placeholder:text-gray-400 transition-colors"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : inputValue ? (
            <button type="button" onClick={handleClear} className="p-0.5 text-gray-400 hover:text-gray-600" tabIndex={-1}>
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown suggestions */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-[99999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 flex items-start gap-2 transition-colors"
            >
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-gray-900">
                  {s.structured_formatting?.main_text || s.description}
                </span>
                {s.structured_formatting?.secondary_text && (
                  <span className="text-gray-500 text-xs block mt-0.5">
                    {s.structured_formatting.secondary_text}
                  </span>
                )}
              </div>
            </button>
          ))}
          <div className="px-4 py-1.5 text-[10px] text-gray-400 text-right">
            Powered by Google
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-xs text-amber-600">{error}</p>}
      {isReady && !error && !inputValue && (
        <p className="mt-1 text-xs text-gray-400">Type at least 3 characters to search</p>
      )}
    </div>
  );
}
